import React, { useState, useEffect, useRef } from "react";
import { confirmCreditCharge } from "../api/creditsApi";
import { fixManimVideoUrl, toAbsoluteRenderUrl } from "../utils/videoUrl";
import { getCachedBgmConfig } from "../api/bgmApi";
import SegmentPreview from "./SegmentPreview";
import "./AnimationPlayer.css"; // 导入新的CSS文件

const AnimationPlayer = ({
  code,
  isLoading,
  error,
  onRenderError,
  onRenderFailure,
  onRenderStatusChange,
  onRenderResultChange,
  restoredRenderResult = null,
  onRestoredRenderResultUsed,
  mode = "manim",
  prompt = null,
  title = null,
  description = null,
  category = "general",
  difficulty = "medium",
  tags = null,
  created_by = null,
  chargeSession = null,
  onFinalResultDelivered = null,
  showRenderError = false,
  // 可覆盖的错误提示文案（代码级错误时显示，适用于嵌入到抽屉等非主界面场景）
  renderErrorHint = null,
  // 批量渲染成功后回调，将片段信息向上传递给父组件（用于片段微调）
  // 参数：{ segmentUrls: string[], sceneNames: string[], requestId: string }
  onSegmentInfoChange = null,
  isMuted = false,
  bgm = "deliberate_thought",
}) => {
  // 添加渲染状态跟踪
  const [renderStatus, setRenderStatus] = useState("idle"); // idle, queued, processing, finished, error
  // 服务端返回的真实 manim 错误（仅对 error_type==="code_error" 时有值），供微调 AI 使用
  const [manimError, setManimError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [renderResult, setRenderResult] = useState(null);
  const [renderProgress, setRenderProgress] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const [renderingTime, setRenderingTime] = useState(0);
  const [renderTimeout, setRenderTimeout] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [serverStatus, setServerStatus] = useState(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState("16 / 9");
  // 锁定当前播放 URL，避免 oss_video_url 到来时 key 变化导致 <video> 重建闪烁
  const [displayVideoUrl, setDisplayVideoUrl] = useState(null);
  const displayVideoUrlRef = useRef(null);
  const restoredUsedKeyRef = useRef(null);
  const restoredInProgressUsedKeyRef = useRef(null);
  const lastRenderedCodeRef = useRef(null);
  const resumePollRef = useRef({ cancelled: false, timeoutId: null, lastStatus: null });
  const confirmedChargeSessionRef = useRef(null);
  const deliveredResultRef = useRef(null);
  const showcasePollRef = useRef({ requestId: null, cancelled: false, timeoutId: null });

  useEffect(() => {
    setVideoAspectRatio("16 / 9");
  }, [displayVideoUrl]);

  // 使用Ref保存最新的元数据，避免闭包陷阱（特别是window.applyFixedCode调用旧renderCode时）
  const metadataRef = useRef({
    prompt,
    title,
    description,
    category,
    difficulty,
    tags,
    created_by,
    bgm,
    chargeSession,
  });

  // 每次渲染更新Ref
  useEffect(() => {
    metadataRef.current = {
      prompt,
      title,
      description,
      category,
      difficulty,
      tags,
      created_by,
      bgm,
      chargeSession,
    };
  }, [prompt, title, description, category, difficulty, tags, created_by, bgm, chargeSession]);

  // 检查错误是否为适合AI修复的代码级别错误
  // 优先使用服务端返回的 error_type 字段；若无则退回关键字匹配
  const isCodeError = (errorMsg, errorType = null) => {
    if (!errorMsg) return false;
    // 服务端已明确标记类型时直接使用
    if (errorType === "code_error") return true;
    if (errorType === "service_error") return false;
    // 退回关键字匹配（兼容旧版服务端）
    const nonCodeErrorKeywords = [
      "超时", // timeout
      "网络", // network
      "服务器", // server
      "连接", // connection
      "轮询", // poll
      "找不到", // not found
      "不存在", // not exist
      "已过期", // expired
      "无法解析", // cannot parse
      "invalid character", // 无效字符错误，通常由非代码内容(用户乱输入)引起
      "使用人数", // 服务端脱敏消息，非代码错误
    ];
    for (const keyword of nonCodeErrorKeywords) {
      if (String(errorMsg).includes(keyword)) {
        return false;
      }
    }
    return true;
  };

  // 定义渲染服务器URL
  const RENDER_SERVER_URL = process.env.REACT_APP_RENDER_SERVER_URL;

  const normalizeVideoUrl = (url) => {
    if (!url) return null;
    return toAbsoluteRenderUrl(fixManimVideoUrl(url), RENDER_SERVER_URL);
  };

  const receiveRenderProgress = (payload) => {
    if (Array.isArray(payload?.preview_segment_urls)) {
      setRenderProgress(payload);
    }
  };
  const hasSegmentPreview = renderProgress?.preview_segment_urls?.some(Boolean) &&
    (!requestId || renderProgress.request_id === requestId);

  const getDownloadVideoUrl = (result) => {
    if (!result?.request_id || !RENDER_SERVER_URL) return null;
    return `${RENDER_SERVER_URL.replace(/\/$/, "")}/download/${result.request_id}?prefer_watermark=true`;
  };

  const getVideoUrlCandidates = (result) => {
    if (!result) return [];
    if (process.env.REACT_APP_LOCAL_RENDER === 'true') {
      return [getDownloadVideoUrl(result)].filter(Boolean);
    }

    // 生产环境中 /download 通常是下载流，浏览器可能无法 Range seek。
    // 播放优先使用 OSS/作品地址或 /media 静态地址，最后才回退到 /download。
    return [
      result.oss_video_url,
      result.media_url,
      result.watermarked_video_url,
      result.pending_oss_video_url,
      result.video_url,
      getDownloadVideoUrl(result),
    ]
      .map(normalizeVideoUrl)
      .filter((url, index, urls) => url && urls.indexOf(url) === index);
  };

  const getInitialVideoUrl = (result) => {
    return getVideoUrlCandidates(result)[0] || null;
  };

  const mergeRenderResult = (nextResult) => {
    setRenderResult((prev) => {
      const merged = { ...(prev || {}), ...(nextResult || {}) };
      if (onRenderResultChange) onRenderResultChange(merged);
      return merged;
    });
  };

  // 批量渲染成功后将片段信息向上传递给父组件，供片段微调使用
  useEffect(() => {
    if (
      renderResult?.success &&
      renderResult?.status === "finished" &&
      Array.isArray(renderResult?.segment_urls) &&
      renderResult.segment_urls.length > 0 &&
      onSegmentInfoChange
    ) {
      onSegmentInfoChange({
        segmentUrls: renderResult.segment_urls,
        sceneNames: renderResult.scene_names || [],
        requestId: renderResult.request_id || null,
      });
    }
  }, [renderResult, onSegmentInfoChange]);

  // 首次有可用 URL 时锁定 displayVideoUrl；renderResult 清空（新渲染开始）时重置。
  // 目的：OSS 地址在渲染完成约 1s 后才到，不能让它改变 key 导致 <video> 重建闪烁。
  useEffect(() => {
    if (!renderResult) {
      setDisplayVideoUrl(null);
      displayVideoUrlRef.current = null;
      return;
    }
    if (!displayVideoUrlRef.current) {
      const url = getInitialVideoUrl(renderResult);
      if (url) {
        displayVideoUrlRef.current = url;
        setDisplayVideoUrl(url);
      }
    }
  }, [renderResult]);

  const confirmFinalVideoDelivered = async (result) => {
    if (result?.success !== true) return;
    const deliveryKey = result?.request_id || result?.oss_video_url || result?.media_url || getInitialVideoUrl(result);
    if (deliveryKey && deliveredResultRef.current === deliveryKey) return;

    const sessionId = chargeSession?.sessionId;
    let confirmResult = null;
    if (sessionId && confirmedChargeSessionRef.current !== sessionId) {
      confirmedChargeSessionRef.current = sessionId;
      confirmResult = await confirmCreditCharge({
        sessionId,
        resultType: "video",
        resultRef: result?.request_id || result?.oss_video_url || result?.media_url || null,
      });
      if (!confirmResult.success) {
        confirmedChargeSessionRef.current = null;
        setRenderError(confirmResult.message || "确认扣费失败，请重试");
        return;
      }
    }

    if (deliveryKey) {
      deliveredResultRef.current = deliveryKey;
    }
    if (onFinalResultDelivered) {
      onFinalResultDelivered(result, confirmResult);
    }
  };

  // 用于追踪当前渲染任务的清理函数
  const cleanupRef = React.useRef(null);
  useEffect(() => () => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const resumeExistingRender = (existingRequestId, initialStatus = "processing") => {
    if (!existingRequestId) return () => {};

    // 取消之前的 resume 轮询
    resumePollRef.current.cancelled = true;
    if (resumePollRef.current.timeoutId) {
      clearTimeout(resumePollRef.current.timeoutId);
      resumePollRef.current.timeoutId = null;
    }

    const pollState = { cancelled: false, timeoutId: null, lastStatus: null };
    resumePollRef.current = pollState;

    setIsAnimating(true);
    setRenderResult(null);
    setRenderProgress(null);
    setRenderTimeout(false);
    setRenderingTime(0);
    setRenderError(null);
    setRequestId(existingRequestId);
    setRenderStatus(initialStatus || "processing");
    if (onRenderStatusChange) onRenderStatusChange(initialStatus || "processing");

    // 把“渲染中”的信息也同步给父组件，确保离开页面时能保存 request_id
    if (onRenderResultChange) {
      onRenderResultChange({
        success: false,
        status: initialStatus || "processing",
        request_id: existingRequestId,
      });
    }

    const startTime = Date.now();
    const timerInterval = setInterval(() => {
      if (pollState.cancelled) return;
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      setRenderingTime(elapsedSeconds);
    }, 1000);

    const poll = async () => {
      if (pollState.cancelled) return;
      try {
        const res = await fetch(`${RENDER_SERVER_URL}/render_status/${existingRequestId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const statusData = await res.json();
        if (pollState.cancelled) return;
        receiveRenderProgress(statusData);

        // 成功完成：直接设置结果并停止
        if (statusData?.success) {
          setRenderResult(statusData);
          if (onRenderResultChange) onRenderResultChange(statusData);
          setRenderStatus("finished");
          setIsAnimating(false);
          setRequestId(null);
          setRenderError(null);
          clearInterval(timerInterval);
          if (onRenderStatusChange) onRenderStatusChange("finished");
          return;
        }

        const terminalErrorStatuses = new Set([
          "error",
          "timeout",
          "cancelled",
          "stuck_timeout",
          "queue_full",
          "invalid_code",
          "unknown",
        ]);
        if (terminalErrorStatuses.has(statusData?.status)) {
          setRenderError(statusData?.error || "渲染失败");
          setManimError(statusData?.manim_error || null);
          setRenderStatus("error");
          setIsAnimating(false);
          setRequestId(null);
          clearInterval(timerInterval);
          if (onRenderStatusChange) onRenderStatusChange("error");
          return;
        }

        // 继续处理中/排队中：更新状态并继续轮询
        const nextStatus = statusData?.status || "processing";
        setRenderError(null);
        setManimError(null); // 收到正常状态时清除可能存在的错误提示
        setRenderStatus(nextStatus);
        if (onRenderStatusChange) onRenderStatusChange(nextStatus);

        // 仅当状态变化时向父组件同步，降低写入频率
        const progressKey = `${nextStatus}:${statusData.completed_scene_count || 0}`;
        if (progressKey !== pollState.lastStatus) {
          pollState.lastStatus = progressKey;
          if (onRenderResultChange) {
            onRenderResultChange({
              ...statusData,
              success: false,
              status: nextStatus,
              request_id: existingRequestId,
            });
          }
        }

        pollState.timeoutId = setTimeout(poll, 3000);
      } catch (e) {
        if (pollState.cancelled) return;
        // 轮询失败时保留 requestId（不清空），确保 onRenderFailure 不触发 UI 重置。
        // 渲染服务器上的任务可能仍在后台运行，用户可通过【检查渲染状态】按钮继续跟踪。
        setRenderError(`检查渲染状态失败: ${e.message}。任务可能仍在后台渲染，可点击【检查渲染状态】按钮查询。`);
        setIsAnimating(false);
        if (onRenderStatusChange) onRenderStatusChange("error");
        clearInterval(timerInterval);
      }
    };

    pollState.timeoutId = setTimeout(poll, 0);

    // 返回清理函数
    return () => {
      pollState.cancelled = true;
      if (pollState.timeoutId) clearTimeout(pollState.timeoutId);
      clearInterval(timerInterval);
    };
  };

  // 当有新的代码生成时，初始化组件状态
  useEffect(() => {
    // Consuming the restored-result prop must not cancel the resumed poller.
    // Code changes clean up below; actual unmount is handled separately.
    if (code && !restoredRenderResult && code === lastRenderedCodeRef.current) return;
    if (code === lastRenderedCodeRef.current && restoredRenderResult?.request_id &&
        restoredRenderResult.success !== true &&
        restoredInProgressUsedKeyRef.current === `${mode}:${restoredRenderResult.request_id}`) {
      receiveRenderProgress(restoredRenderResult);
      return;
    }
    // 如果有旧的渲染任务，先清理它
    if (cleanupRef.current) {
      console.log("清理旧的渲染任务");
      cleanupRef.current();
      cleanupRef.current = null;
    }
    restoredInProgressUsedKeyRef.current = null;

    // 关键修正：每当code变化时，必须强制清除父组件的错误状态
    // 这样可以防止之前失败的renderError残留，导致用户在生成新动画时看到旧的错误界面
    if (onRenderError) {
      onRenderError(null);
    }

    if (code) {
      // 如果恢复的是“渲染中/排队中”的任务（有 request_id 但还没出视频），继续跟踪同一个 request_id，禁止重新提交渲染
      const isRestoredInProgress =
        restoredRenderResult &&
        restoredRenderResult.request_id &&
        restoredRenderResult.success !== true &&
        !restoredRenderResult.media_url &&
        !restoredRenderResult.oss_video_url &&
        !restoredRenderResult.video_url &&
        !restoredRenderResult.watermarked_video_url &&
        !restoredRenderResult.pending_oss_video_url &&
        (restoredRenderResult.status === "queued" ||
          restoredRenderResult.status === "processing" ||
          restoredRenderResult.status === "timeout");

      if (isRestoredInProgress) {
        const restoreInProgressKey = `${mode}:${restoredRenderResult.request_id}`;
        if (restoredInProgressUsedKeyRef.current !== restoreInProgressKey) {
          restoredInProgressUsedKeyRef.current = restoreInProgressKey;
          cleanupRef.current = resumeExistingRender(
            restoredRenderResult.request_id,
            restoredRenderResult.status || "processing"
          );
          receiveRenderProgress(restoredRenderResult);
          if (onRestoredRenderResultUsed) onRestoredRenderResultUsed();
          lastRenderedCodeRef.current = code;
        }
        return;
      }

      // 如果父组件恢复了已渲染完成的结果（例如从“仓库”返回），优先直接复用，避免重复渲染
      const hasRestoredPlayableUrl =
        restoredRenderResult &&
        restoredRenderResult.success !== false &&
        (restoredRenderResult.request_id ||
          restoredRenderResult.oss_video_url ||
          restoredRenderResult.video_url ||
          restoredRenderResult.media_url ||
          restoredRenderResult.watermarked_video_url ||
          restoredRenderResult.pending_oss_video_url);

      if (hasRestoredPlayableUrl) {
        const restoreKey = `${mode}:${restoredRenderResult.request_id || "no_request_id"}:${code.length}`;
        if (restoredUsedKeyRef.current !== restoreKey) {
          restoredUsedKeyRef.current = restoreKey;

          setIsAnimating(false);
          setRenderStatus("finished");
          setRenderResult(restoredRenderResult);
          setRenderError(null);
          setRenderTimeout(false);
          setRenderingTime(0);
          setRequestId(null);
          setIsCheckingStatus(false);
          setServerStatus(null);

          if (onRenderStatusChange) onRenderStatusChange("finished");
          if (onRenderResultChange) onRenderResultChange(restoredRenderResult);
          if (onRestoredRenderResultUsed) onRestoredRenderResultUsed();
          lastRenderedCodeRef.current = code;
          return;
        }
      }

      setIsAnimating(true);
      setRenderResult(null);
      setRenderProgress(null);
      if (onRenderResultChange) onRenderResultChange(null);
      setRenderError(null);
      setRenderTimeout(false);
      setRenderingTime(0);
      setRequestId(null);
      setIsCheckingStatus(false);
      setServerStatus(null);

      // 保存清理函数
      lastRenderedCodeRef.current = code;
      cleanupRef.current = renderCode(code);
    } else {
      lastRenderedCodeRef.current = null;
      // code 为空时，重置所有状态
      setIsAnimating(false);
      setRenderResult(null);
      setRenderProgress(null);
      if (onRenderResultChange) onRenderResultChange(null);
      setRenderError(null);
      setRenderTimeout(false);
      setRenderingTime(0);
      setRequestId(null);
      setIsCheckingStatus(false);
      setServerStatus(null);
    }

    // The next code/task cleans up at the start of this effect; unmount has
    // its own effect so clearing a consumed restored result keeps polling.
  }, [code, restoredRenderResult]);

  // 修改：当渲染错误状态更新时立即通知父组件
  useEffect(() => {
    if (!renderError) return;

    // showRenderError=true 表示组件嵌入在微调抽屉等场景，始终上报错误（含 manimError）供上层使用
    if (showRenderError && onRenderError) {
      onRenderError(renderError, manimError);
      return;
    }

    // 普通 manim/manimgx/solve 模式静默回到初始态，不向页面暴露渲染错误详情。
    if (mode === "manim" || mode === "manimgx" || mode === "solve") return;

    // 仅当错误是代码级别问题时，才调用 onRenderError
    if (onRenderError && isCodeError(renderError)) {
      console.log("检测到代码级渲染错误，通知父组件:", renderError);
      onRenderError(renderError, manimError);
    }
  }, [renderError, manimError, onRenderError, mode, showRenderError]);

  // 对于 manim/manimgx/solve 模式，任何渲染错误都交由父组件静默回退到初始态。
  // 即使服务端返回了 requestId，也不能停留在无结果的预览面板。
  useEffect(() => {
    if (!renderError) return;
    if (hasSegmentPreview) return;
    if ((mode === "manim" || mode === "manimgx" || mode === "solve") && onRenderFailure) {
      onRenderFailure({ mode, error: String(renderError) });
    }
  }, [renderError, mode, onRenderFailure, hasSegmentPreview]);

  // 测试渲染服务器连接（支持健康检查）
  const testServerConnection = () => {
    setIsTesting(true);
    setServerStatus(null);

    // 显示正在测试的消息
    console.log("正在测试渲染服务器连接...");

    // 记录当前时间，以便测量响应时间
    const startTime = Date.now();

    // 首先尝试健康检查端点，获取更详细的状态信息
    fetch(`${RENDER_SERVER_URL}/health`, {
      method: "GET",
      mode: "cors",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then((response) => {
        const responseTime = Date.now() - startTime;
        console.log(`服务器响应时间: ${responseTime}ms`);
        
        if (response.ok) {
          return response.json().then((data) => {
            console.log("健康检查结果:", data);
            
            // 根据服务器状态构建消息
            let message = `服务器连接成功（${responseTime}ms）`;
            let success = true;
            
            if (data.render_executor) {
              const slots = data.render_executor.available_slots;
              const active = data.render_executor.active_tasks;
              
              if (slots <= 0) {
                message = `服务器繁忙：${active}个渲染任务进行中，新请求将排队等待（${responseTime}ms）`;
                success = true; // 仍然是成功的，只是繁忙
              } else {
                message = `服务器就绪：${slots}个可用渲染槽位（${responseTime}ms）`;
              }
            }
            
            setServerStatus({ success, message, details: data });
          });
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      })
      .catch((healthError) => {
        console.log("健康检查失败，尝试ping:", healthError.message);
        
        // 如果健康检查失败，回退到简单的ping
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${RENDER_SERVER_URL}/ping`, true);
        xhr.timeout = 10000;
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");

        xhr.onload = function () {
          const responseTime = Date.now() - startTime;
          if (xhr.status >= 200 && xhr.status < 300) {
            setServerStatus({
              success: true,
              message: `服务器连接成功：${responseTime}ms`,
            });
          } else {
            setServerStatus({
              success: false,
              message: `服务器响应错误：${xhr.status} ${xhr.statusText}`,
            });
          }
          setIsTesting(false);
        };

        xhr.onerror = function (e) {
          console.error("服务器连接测试错误:", e);
          setServerStatus({
            success: false,
            message: `服务器连接失败：可能是服务器未启动或网络问题`,
          });
          setIsTesting(false);
        };

        xhr.ontimeout = function () {
          setServerStatus({
            success: false,
            message: `服务器连接超时（10秒）：服务器可能阻塞或未响应`,
          });
          setIsTesting(false);
        };

        try {
          xhr.send();
        } catch (e) {
          setServerStatus({
            success: false,
            message: `发送请求失败: ${e.message}`,
          });
          setIsTesting(false);
        }
      })
      .finally(() => {
        setIsTesting(false);
      });
  };

  // 渲染代码的函数，抽取为单独函数便于重用
  // 返回清理函数，用于取消正在进行的渲染
  const renderCode = (codeToRender) => {
    // 标记此次渲染是否已被取消
    let isCancelled = false;
    let pollTimeoutId = null;
    let queueTimerInterval = null;

    setIsAnimating(true);
    setRenderStatus("idle");
    setRenderResult(null);
    setRenderProgress(null);
    if (onRenderResultChange) onRenderResultChange(null);
    setRenderError(null);
    setManimError(null); // 清空本地渲染错误
    if (onRenderStatusChange) {
      onRenderStatusChange("started");
    }
    if (onRenderError) {
      onRenderError(null); // 通知父组件清空渲染错误，以便正确切换面板
    }
    setRenderTimeout(false);

    console.log("正在发送代码到渲染服务器...");

    // 之前这里会生成本地 clientRequestId，但查询状态时会导致404，因此已移除

    // 添加计时器，显示渲染已经进行的时间
    const startTime = Date.now();
    const timerInterval = setInterval(() => {
      if (isCancelled) return;
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      setRenderingTime(elapsedSeconds);
    }, 1000);

    // 添加超时处理，增加到210秒（比服务器180秒稍长）
    const timeoutId = setTimeout(() => {
      if (isCancelled) return;
      clearInterval(timerInterval);
      setIsAnimating(false);
      setRenderTimeout(true);
      // 不再保存本地生成的 clientRequestId，等待服务器返回真正的 request_id
      const errorMsg =
        "渲染超时（210秒）。动画可能仍在后台渲染，您可以点击【检查渲染状态】按钮查询最终结果。";
      setRenderError(errorMsg);
      if (onRenderStatusChange) {
        onRenderStatusChange("error");
      }
      // 不需要额外调用onRenderError，因为useEffect会处理
    }, 300000); // 300秒超时

    // 使用XMLHttpRequest替代fetch
    const xhr = new XMLHttpRequest();
    let eventSource = null;
    
    // 清理函数：取消渲染任务
    const cleanup = () => {
      console.log("取消渲染任务");
      isCancelled = true;
      clearTimeout(timeoutId);
      clearInterval(timerInterval);
      if (pollTimeoutId) {
        clearTimeout(pollTimeoutId);
        pollTimeoutId = null;
      }
      if (queueTimerInterval) {
        clearInterval(queueTimerInterval);
        queueTimerInterval = null;
      }
      if (eventSource) {
        try {
          eventSource.close();
        } catch (e) {
          // 忽略 close 错误
        }
        eventSource = null;
      }
      try {
        xhr.abort();
      } catch (e) {
        // 忽略 abort 错误
      }
    };

    // 添加更多诊断日志
    console.log(`正在准备连接到 ${RENDER_SERVER_URL}/render`);

    // 打开连接
    xhr.open("POST", `${RENDER_SERVER_URL}/render`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    xhr.timeout = 210000; // 210秒超时
    // 移除跨域凭证支持，可能导致预检请求失败
    // xhr.withCredentials = true;

    // 监听readyState变化以获取更详细的连接过程
    xhr.onreadystatechange = function () {
      console.log(
        `XHR readyState变化: ${xhr.readyState} (${
          ["未初始化", "已打开", "已发送", "加载中", "已完成"][xhr.readyState]
        })`
      );
    };

    xhr.onload = function () {
      clearTimeout(timeoutId);
      clearInterval(timerInterval);

      // 如果已被取消，不更新状态
      if (isCancelled) {
        console.log("渲染已被取消，忽略响应");
        return;
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);

          console.log("渲染结果:", data);
          // 无论成功还是失败，都尝试保存request_id以便后续状态检查
          if (data.request_id) {
            console.log("保存服务器返回的request_id:", data.request_id);
            setRequestId(data.request_id);
          }

          if (data.success) {
            console.log("渲染成功:", data);
            console.log("保存的renderResult中的request_id:", data.request_id);
            setRenderResult(data);
            if (onRenderResultChange) onRenderResultChange(data);
            setRenderStatus("finished");
            setIsAnimating(false);
            if (onRenderStatusChange) {
              onRenderStatusChange("finished");
            }
            // 渲染成功时明确清空错误，避免出现之前的错误信息
            setRenderError(null);
            // 成功时清空requestId，不需要再检查状态
            setRequestId(null);
          } else if (data.status === "timeout" && data.request_id) {
            // 处理服务器端超时但任务仍在后台进行的情况
            console.log("渲染超时，但可以稍后检查:", data.request_id);
            setRenderTimeout(true);
            setIsAnimating(false);
            setRequestId(data.request_id);
            setRenderError(
              data.error || "渲染超时，但任务仍在后台进行。您可以稍后检查状态。"
            );
            if (onRenderStatusChange) {
              onRenderStatusChange("error");
            }
          } else if (data.status === "queued" && data.request_id) {
            // 服务器已排队渲染，优先使用SSE推送
            console.log("任务排队，尝试SSE推送", data.request_id);
            setRenderStatus("queued");
            setRequestId(data.request_id);
            if (onRenderStatusChange) {
              onRenderStatusChange("queued");
            }
            if (onRenderResultChange) {
              onRenderResultChange({
                success: false,
                status: "queued",
                request_id: data.request_id,
              });
            }
            
            // 重新启动计时器，避免计时中断
            const queueStartTime = Date.now();
            queueTimerInterval = setInterval(() => {
              const elapsedSeconds = Math.floor((Date.now() - queueStartTime) / 1000);
              setRenderingTime(elapsedSeconds);
            }, 1000);
            
            let pollCount = 0;
            const maxPolls = 100; // 设置最大轮询次数，例如100次 (约5分钟)

            const poll = () => {
              if (isCancelled) return;
              if (pollCount++ > maxPolls) {
                setRenderError("轮询超时（超过5分钟），任务可能仍在后台渲染。请稍后手动检查状态。");
                setIsAnimating(false);
                clearInterval(queueTimerInterval);
                // 保留requestId以便手动检查
                return;
              }

              fetch(`${RENDER_SERVER_URL}/render_status/${data.request_id}`)
                .then((res) => res.json())
                .then((statusData) => {
                  if (isCancelled) return;
                  receiveRenderProgress(statusData);
                  console.log("轮询结果", statusData);
                  if (statusData.success) {
                    setRenderResult(statusData);
                    if (onRenderResultChange) onRenderResultChange(statusData);
                    setRenderStatus("finished");
                    setIsAnimating(false);
                    clearInterval(queueTimerInterval); // 清理队列计时器
                    setRenderError(null); // 明确清除渲染错误
                    setRequestId(null);
                    if (onRenderStatusChange) {
                      onRenderStatusChange("finished");
                    }
                  } else if (
                    statusData.status === "processing" ||
                    statusData.status === "queued"
                  ) {
                    setRenderError(null); // 收到正常状态时清除可能存在的错误提示
                    setRenderStatus(statusData.status); // 更新为最新状态
                    if (onRenderStatusChange) {
                      onRenderStatusChange(statusData.status);
                    }
                    if (onRenderResultChange) {
                      onRenderResultChange({
                        ...statusData,
                        success: false,
                        status: statusData.status,
                        request_id: data.request_id,
                      });
                    }
                    pollTimeoutId = setTimeout(poll, 3000);
                  } else {
                    setRenderError(statusData.error || "渲染失败");
                    setManimError(statusData.manim_error || null);
                    setIsAnimating(false);
                    clearInterval(queueTimerInterval); // 清理队列计时器
                    setRequestId(null);
                    if (onRenderStatusChange) {
                      onRenderStatusChange("error");
                    }
                  }
                })
                .catch((err) => {
                  if (isCancelled) return;
                  // 保留 requestId：网络波动导致的轮询失败并不等于渲染失败。
                  // 渲染服务器上的任务可能仍在运行，保留 requestId 可让用户
                  // 通过【检查渲染状态】按钮或页面刷新后继续跟踪，同时阻止
                  // onRenderFailure 触发 UI 完全重置（见 useEffect[renderError]）。
                  setRenderError(`轮询状态错误: ${err.message}。任务可能仍在后台渲染，可点击【检查渲染状态】按钮查询。`);
                  setIsAnimating(false);
                  clearInterval(queueTimerInterval);
                  if (onRenderStatusChange) {
                    onRenderStatusChange("error");
                  }
                });
            };

            const startPolling = () => {
              if (isCancelled) return;
              console.log("SSE不可用，回退到轮询");
              poll();
            };

            if (window.EventSource) {
              try {
                const sseUrl = `${RENDER_SERVER_URL}/render_events/${data.request_id}`;
                eventSource = new EventSource(sseUrl);

                eventSource.onmessage = (event) => {
                  if (isCancelled) return;
                  try {
                    const statusData = JSON.parse(event.data);
                    receiveRenderProgress(statusData);
                    console.log("SSE推送结果", statusData);

                    if (statusData.success) {
                      setRenderResult(statusData);
                      if (onRenderResultChange) onRenderResultChange(statusData);
                      setRenderStatus("finished");
                      setIsAnimating(false);
                      clearInterval(queueTimerInterval);
                      setRenderError(null); // 明确清除渲染错误
                      setRequestId(null);
                      if (onRenderStatusChange) {
                        onRenderStatusChange("finished");
                      }
                      eventSource.close();
                      eventSource = null;
                      return;
                    }

                    if (
                      statusData.status === "processing" ||
                      statusData.status === "queued"
                    ) {
                      setRenderError(null); // 收到正常状态时清除可能存在的错误提示
                      setRenderStatus(statusData.status);
                      if (onRenderStatusChange) {
                        onRenderStatusChange(statusData.status);
                      }
                      if (onRenderResultChange) {
                        onRenderResultChange({
                          ...statusData,
                          success: false,
                          status: statusData.status,
                          request_id: data.request_id,
                        });
                      }
                      return;
                    }

                    setRenderError(statusData.error || "渲染失败");
                    setManimError(statusData.manim_error || null);
                    setIsAnimating(false);
                    clearInterval(queueTimerInterval);
                    setRequestId(null);
                    if (onRenderStatusChange) {
                      onRenderStatusChange("error");
                    }
                    eventSource.close();
                    eventSource = null;
                  } catch (e) {
                    console.error("解析SSE数据失败:", e);
                  }
                };

                eventSource.onerror = () => {
                  if (isCancelled) return;
                  if (eventSource) {
                    eventSource.close();
                    eventSource = null;
                  }
                  startPolling();
                };
              } catch (e) {
                console.error("SSE初始化失败:", e);
                startPolling();
              }
            } else {
              startPolling();
            }
          } else {
            console.error("渲染失败:", data.error);
            // 即使失败，如果有request_id也保存，可能任务还在后台运行
            if (data.request_id) {
              setRenderTimeout(true);
              setRenderError(
                (data.error || "渲染失败") +
                  "\n\n您可以稍后点击【检查渲染状态】查看是否完成。"
              );
              if (onRenderStatusChange) {
                onRenderStatusChange("error");
              }
            } else {
              setRenderError(data.error || "渲染失败");
              setIsAnimating(false);
              if (onRenderStatusChange) {
                onRenderStatusChange("error");
              }
            }
          }
        } catch (e) {
          console.error("解析响应失败:", e);
          console.error("原始响应:", xhr.responseText);
          setRenderError(
            `无法解析服务器响应: ${e.message} (响应长度: ${xhr.responseText.length}字节)`
          );
          setIsAnimating(false);
          if (onRenderStatusChange) {
            onRenderStatusChange("error");
          }
        }
      } else {
        console.error("服务器响应错误:", xhr.status, xhr.statusText);
        console.error("响应头:", xhr.getAllResponseHeaders());
        setRenderError(`服务器响应错误: ${xhr.status} ${xhr.statusText}`);
        setIsAnimating(false);
        if (onRenderStatusChange) {
          onRenderStatusChange("error");
        }
      }
    };

    xhr.onerror = function (e) {
      clearTimeout(timeoutId);
      clearInterval(timerInterval);
      
      // 如果已被取消，不更新状态
      if (isCancelled) {
        console.log("渲染已被取消，忽略错误");
        return;
      }
      
      setIsAnimating(false);
      console.error("网络错误", e);
      // 输出更详细的错误信息以便调试
      console.error("详细错误信息:", {
        readyState: xhr.readyState,
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText,
        responseURL: xhr.responseURL,
      });

      // 尝试使用不同的请求方法进行测试
      console.log("尝试使用普通fetch请求测试...");

      fetch(`${RENDER_SERVER_URL}/ping`, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.text())
        .then((data) => {
          console.log("Fetch请求成功:", data);
          setRenderError(`
          网络连接错误 (XMLHttpRequest失败但Fetch可用)
          
          可能原因:
          1. XMLHttpRequest配置问题
          2. 跨域请求(CORS)设置不完整
          
          解决方法:
          1. 确保服务器正确配置CORS头部
          2. 检查浏览器控制台获取更详细错误信息
          3. 尝试重启渲染服务器和浏览器
        `);
        })
        .catch((err) => {
          console.error("Fetch请求也失败:", err);
          setRenderError(`
          网络连接错误
          
          可能原因:
          1. 渲染服务器未启动
          2. 端口号5000被占用或被防火墙阻止
          3. 跨域请求(CORS)问题
          
          解决方法:
          1. 确保已运行 python manim_server.py
          2. 检查 manim_server_log.txt 日志
          3. 尝试手动访问 http://localhost:5000 测试服务器
          4. 确保服务器允许跨域请求
          5. 尝试使用其他浏览器测试
        `);
        });
      if (onRenderStatusChange) {
        onRenderStatusChange("error");
      }
    };

    xhr.ontimeout = function () {
      clearTimeout(timeoutId);
      clearInterval(timerInterval);
      
      // 如果已被取消，不更新状态
      if (isCancelled) {
        console.log("渲染已被取消，忽略超时");
        return;
      }
      
      setIsAnimating(false);
      setRenderTimeout(true);
      console.error("请求超时");
      setRenderError(
        "请求超时，请确保渲染服务器正在运行。3D动画可能需要更长时间渲染，建议重试并等待更长时间。"
      );
      if (onRenderStatusChange) {
        onRenderStatusChange("error");
      }
    };

    // 发送请求
    try {
      console.log("正在发送渲染请求...");
      
      // 准备请求数据，包含代码和元数据
    const requestData = {
      code: codeToRender,
      mode: mode || "manim",
      prompt: metadataRef.current.prompt,
      title: metadataRef.current.title,
      description: metadataRef.current.description,
      category: metadataRef.current.category,
      difficulty: metadataRef.current.difficulty,
      tags: metadataRef.current.tags,
      created_by: metadataRef.current.created_by,
      charge_session_id: metadataRef.current?.chargeSession?.sessionId || chargeSession?.sessionId || null,
      auto_split: true,
      bgm: metadataRef.current?.bgm !== undefined ? metadataRef.current.bgm : "deliberate_thought",
      bgm_config: (function() {
        const c = getCachedBgmConfig() || {};
        return {
          silent_video_volume: c.silent_video_volume,
          voiceover_bgm_volume: c.voiceover_bgm_volume,
          ducking_enabled: c.ducking_enabled,
          fade_in_duration: c.fade_in_duration,
          fade_out_duration: c.fade_out_duration,
        };
      })(),
    };
      
    const jsonData = JSON.stringify(requestData);
      console.log(`请求数据大小: ${jsonData.length} 字节`);
      xhr.send(jsonData);
    } catch (e) {
      clearTimeout(timeoutId);
      clearInterval(timerInterval);
      if (!isCancelled) {
        setIsAnimating(false);
        console.error("发送请求失败:", e);
        setRenderError(`发送请求失败: ${e.message}`);
        if (onRenderStatusChange) {
          onRenderStatusChange("error");
        }
      }
    }

    // 返回清理函数
    return cleanup;
  };

  // 检查渲染状态
  const checkRenderStatus = () => {
    if (!requestId) {
      setRenderError("无法检查状态：缺少请求ID。请重新提交渲染请求。");
      return;
    }

    setIsCheckingStatus(true);
    console.log("正在检查渲染状态，requestId:", requestId);

    fetch(`${RENDER_SERVER_URL}/render_status/${requestId}`)
      .then((response) => {
        console.log("状态检查响应:", response.status);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log("状态检查结果:", data);
        receiveRenderProgress(data);
        if (data.success) {
          // 渲染已完成
          console.log("渲染已完成，设置结果");
          setRenderResult(data);
          if (onRenderResultChange) onRenderResultChange(data);
          setRenderError(null);
          setRenderTimeout(false);
          setRequestId(null);
        } else if (data.status === "processing") {
          // 仍在处理中
          setRenderError(
            "渲染仍在进行中，请稍后再检查。3D动画渲染可能需要较长时间。"
          );
        } else if (data.status === "timeout") {
          // 渲染超时
          setRenderError(
            data.error || "渲染超时（5分钟）。可能原因：\n1. 代码计算量过大\n2. 服务器配置问题\n3. Manim版本兼容性问题\n\n请检查代码或联系管理员。"
          );
          setRequestId(null);
        } else if (data.status === "unknown") {
          // 任务不存在或已过期
          setRenderError(
            "渲染任务不存在或已过期。可能是服务器重启或任务已清理。请重新提交渲染请求。"
          );
          setRequestId(null);
        } else {
          // 渲染失败
          setRenderError(data.error || "渲染失败");
          setRequestId(null);
        }
      })
      .catch((err) => {
        console.error("检查状态失败:", err);
        setRenderError(
          `检查状态失败: ${err.message}。请确保渲染服务器正在运行。`
        );
      })
      .finally(() => {
        setIsCheckingStatus(false);
      });
  };

  // 渲染完成时后台可能还在上传 OSS、创建/更新 showcase。继续短轮询，把可下载地址和 showcase_id 合并回前端结果。
  useEffect(() => {
    if (
      !renderResult?.success ||
      !renderResult?.request_id ||
      (renderResult?.showcase_id && renderResult?.oss_video_url)
    ) {
      return undefined;
    }

    const currentRequestId = renderResult.request_id;
    if (showcasePollRef.current.requestId === currentRequestId) {
      return undefined;
    }

    showcasePollRef.current.cancelled = false;
    showcasePollRef.current.requestId = currentRequestId;

    let attempts = 0;
    const maxAttempts = 120;

    const pollShowcase = async () => {
      if (showcasePollRef.current.cancelled) return;
      attempts += 1;

      try {
        const res = await fetch(`${RENDER_SERVER_URL}/render_status/${currentRequestId}`);
        if (res.ok) {
          const statusData = await res.json();
          const hasNewOssVideoUrl =
            statusData?.oss_video_url &&
            statusData.oss_video_url !== renderResult?.oss_video_url;
          const hasNewPendingOssVideoUrl =
            statusData?.pending_oss_video_url &&
            statusData.pending_oss_video_url !== renderResult?.pending_oss_video_url;
          const hasNewMediaUrl =
            statusData?.media_url &&
            statusData.media_url !== renderResult?.media_url;
          const hasNewWatermarkedVideoUrl =
            statusData?.watermarked_video_url &&
            statusData.watermarked_video_url !== renderResult?.watermarked_video_url;
          const hasNewShowcaseId =
            statusData?.showcase_id &&
            statusData.showcase_id !== renderResult?.showcase_id;

          if (
            hasNewOssVideoUrl ||
            hasNewPendingOssVideoUrl ||
            hasNewMediaUrl ||
            hasNewWatermarkedVideoUrl ||
            hasNewShowcaseId
          ) {
            mergeRenderResult(statusData);
          }

          if (statusData?.showcase_id && (statusData?.oss_video_url || statusData?.pending_oss_video_url)) {
            showcasePollRef.current.requestId = null;
            return;
          }
        }
      } catch (err) {
        console.warn("轮询showcase_id失败:", err);
      }

      if (attempts < maxAttempts && !showcasePollRef.current.cancelled) {
        showcasePollRef.current.timeoutId = setTimeout(pollShowcase, 1000);
      } else {
        showcasePollRef.current.requestId = null;
      }
    };

    showcasePollRef.current.timeoutId = setTimeout(pollShowcase, 1000);

    return () => {
      if (showcasePollRef.current.requestId === currentRequestId) {
        showcasePollRef.current.cancelled = true;
        if (showcasePollRef.current.timeoutId) {
          clearTimeout(showcasePollRef.current.timeoutId);
        }
        showcasePollRef.current.requestId = null;
      }
    };
  }, [
    renderResult?.success,
    renderResult?.request_id,
    renderResult?.showcase_id,
    renderResult?.oss_video_url,
    renderResult?.pending_oss_video_url,
    renderResult?.media_url,
    renderResult?.watermarked_video_url,
  ]);

  // 不再创建隐藏 video 预检；正式播放器自身负责加载、备用地址切换与交付确认，
  // 避免弱网用户为同一视频发起两份媒体请求。
  useEffect(() => {
    if (!renderResult || !chargeSession?.sessionId) return;
    confirmFinalVideoDelivered(renderResult);
  }, [chargeSession?.sessionId, renderResult]);

  return (
    <div className="animation-player">
      <div className={`preview-content${displayVideoUrl || hasSegmentPreview ? " has-video" : ""}`}>
        {!code && !isLoading && !error && (
          <div className="empty-state">
            <i className="ri-film-line"></i>
            <p>在左侧描述您的动画需求，AI将为您生成Manim代码并渲染动画</p>

            <button
              className="test-server-btn"
              onClick={testServerConnection}
              disabled={isTesting}
            >
              <i className="ri-server-line"></i>
              {isTesting ? "正在测试连接..." : "测试渲染服务器连接"}
            </button>

            {serverStatus && (
              <div
                className={`server-status ${
                  serverStatus.success ? "success" : "error"
                }`}
              >
                <i
                  className={
                    serverStatus.success ? "ri-check-line" : "ri-close-line"
                  }
                ></i>
                <p>{serverStatus.message}</p>
              </div>
            )}
          </div>
        )}

        {isLoading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>AI正在生成Manim代码...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="error-state">
            <i className="ri-error-warning-line"></i>
            <p>生成代码时出错: {error}</p>
          </div>
        )}

        {code && (
          <div className="animation-container">
            {isAnimating && !hasSegmentPreview && (
              <div className="rendering-state">
                <div className="spinner"></div>
                <p>
                  {renderStatus === "queued"
                    ? "渲染任务已排队，等待处理..."
                    : renderStatus === "processing"
                    ? `正在渲染动画 (${renderingTime}秒)...`
                    : `正在准备渲染 (${renderingTime}秒)...`}
                </p>
                <p className="render-tip">3D场景渲染可能需要较长时间</p>
                {renderStatus === "queued" && (
                  <div className="queue-indicator">
                    <span className="queue-dot"></span>
                    <span className="queue-dot"></span>
                    <span className="queue-dot"></span>
                  </div>
                )}
              </div>
            )}

            {hasSegmentPreview && (
              <SegmentPreview
                key={renderProgress.request_id}
                progress={{ ...renderProgress, ...(renderResult || {}), preview_segment_urls: renderProgress.preview_segment_urls }}
                normalizeUrl={normalizeVideoUrl}
                isMuted={isMuted}
                fullVideoUrl={renderResult?.success ? (getDownloadVideoUrl(renderResult) || renderResult.oss_video_url) : null}
                onPlayFullVideo={() => setRenderProgress(null)}
              />
            )}

            {!hasSegmentPreview && renderResult &&
              renderResult.success !== false &&
              (renderResult.request_id ||
                renderResult.oss_video_url ||
                renderResult.video_url ||
                renderResult.media_url ||
                renderResult.watermarked_video_url ||
                renderResult.pending_oss_video_url) && (
              <div className="video-player">
                <div
                  className="video-frame"
                  style={{ aspectRatio: videoAspectRatio }}
                >
                  <video
                  key={renderResult?.request_id || displayVideoUrl}
                  controls
                  controlsList="nodownload"
                  autoPlay
                  loop
                  preload="metadata"
                  playsInline
                  src={displayVideoUrl}
                  muted={isMuted}
                  onLoadedMetadata={(event) => {
                    const { videoWidth, videoHeight } = event.currentTarget;
                    if (videoWidth > 0 && videoHeight > 0) {
                      setVideoAspectRatio(`${videoWidth} / ${videoHeight}`);
                    }
                  }}
                  onLoadedData={() => {
                    console.log("视频成功加载到播放器");
                    setRenderError(null); // 确保视频成功加载时清除错误
                    confirmFinalVideoDelivered(renderResult);
                  }}
                  onError={(e) => {
                    console.error("视频播放器加载错误:", e);
                    const candidates = getVideoUrlCandidates(renderResult);
                    const currentUrl = displayVideoUrlRef.current;
                    const currentIndex = candidates.findIndex((url) => url === currentUrl);
                    const nextUrl = candidates[currentIndex >= 0 ? currentIndex + 1 : 1];
                    if (!nextUrl) {
                      setRenderError(`视频播放器加载失败`);
                      return;
                    }
                    console.log("尝试切换到备用视频链接...", nextUrl);
                    displayVideoUrlRef.current = nextUrl;
                    setDisplayVideoUrl(nextUrl);
                  }}
                  >
                    您的浏览器不支持视频播放
                  </video>
                </div>
                <div className="video-download-bar">
                  <a
                    className="video-download-btn"
                    href={getDownloadVideoUrl(renderResult) || renderResult?.oss_video_url}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="ri-download-2-line"></i>
                    下载视频
                  </a>
                </div>
              </div>
            )}

            {renderError && (showRenderError || (mode !== "manim" && mode !== "manimgx" && mode !== "solve")) && (
              <div className="render-error">
                <i
                  className={
                    isCodeError(renderError, manimError !== null ? "code_error" : null)
                      ? "ri-error-warning-line"
                      : "ri-time-line"
                  }
                ></i>
                <h3>
                  {isCodeError(renderError, manimError !== null ? "code_error" : null)
                    ? "渲染错误"
                    : "渲染进行中或连接失败"}
                </h3>
                {isCodeError(renderError, manimError !== null ? "code_error" : null) ? (
                  // 代码级错误：友好提示（可通过 prop 覆盖文案）
                  <p className="render-error-hint">
                    {renderErrorHint || "渲染遇到了一些问题，请在左侧面板尝试重新生成。"}
                  </p>
                ) : (
                  // 非代码级错误：显示完整信息
                  <pre>{renderError}</pre>
                )}
                {/* 错误详情输出到控制台 */}
                {isCodeError(renderError, manimError !== null ? "code_error" : null) && console.error("AnimationPlayer渲染错误:", renderError, manimError)}

                {/* 解题模式特殊按钮：重新生成视频 (仅在非超时错误时显示) */}
                {mode === "solve" &&
                  typeof window.regenerateSolveVideo === "function" &&
                  isCodeError(renderError) && (
                    <button
                      className="regenerate-btn" // 使用新的class
                      onClick={() => {
                        console.log("请求重新生成解题视频");
                        // 调用全局函数，该函数在App.js中定义
                        window.regenerateSolveVideo();
                      }}
                      disabled={isAnimating}
                    >
                      <i className="ri-refresh-line"></i>
                      {isAnimating ? "正在重新生成..." : "重新生成视频"}
                    </button>
                  )}

                {(renderTimeout || (renderError && !isCodeError(renderError))) && (
                  <button
                    className="check-status-btn"
                    onClick={checkRenderStatus}
                    disabled={isCheckingStatus}
                  >
                    <i className="ri-refresh-line"></i>
                    {isCheckingStatus ? "正在检查..." : "检查渲染状态"}
                  </button>
                )}

                <button
                  className="test-server-btn"
                  onClick={testServerConnection}
                  disabled={isTesting}
                >
                  <i className="ri-server-line"></i>
                  {isTesting ? "正在测试连接..." : "测试渲染服务器连接"}
                </button>

                {serverStatus && (
                  <div
                    className={`server-status ${
                      serverStatus.success ? "success" : "error"
                    }`}
                  >
                    <i
                      className={
                        serverStatus.success ? "ri-check-line" : "ri-close-line"
                      }
                    ></i>
                    <p>{serverStatus.message}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AnimationPlayer;
